import { DateFilter, Filter } from '@/legacy/types';
import { toISODateTimeLocal } from './dateFormat';
import {
	CUSTOMIZED_FILTER_KEY,
	DATEFILTERS,
	LAST_7_DAYS_FILTER_KEY,
	LAST_30_DAYS_FILTER_KEY,
	LAST_MONTH_FILTER_KEY,
	THIS_MONTH_FILTER_KEY,
	TODAY_FILTER_KEY,
	YESTERDAY_FILTER_KEY,
} from '@/legacy/constants';

export const dateFilterOptions = Object.values(DATEFILTERS).map(({ label, value, dateLogic }) => ({
	label,
	value,
	dateLogic,
}));

export const handleFilterChange = (value: string): DateFilter => {
	const now = new Date();
	let filter: Filter = { startDate: new Date(), endDate: new Date(), dateLogic: 1, label: '' };

	switch (value) {
		case LAST_7_DAYS_FILTER_KEY:
			filter = {
				startDate: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7, 0, 0, 0, 0),
				endDate: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59, 999),
				dateLogic: DATEFILTERS.LAST_7_DAYS_FILTER_KEY.dateLogic,
				label: LAST_7_DAYS_FILTER_KEY,
			};
			break;
		case LAST_30_DAYS_FILTER_KEY:
			filter = {
				startDate: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30, 0, 0, 0, 0),
				endDate: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59, 999),
				dateLogic: DATEFILTERS.LAST_30_DAYS_FILTER_KEY.dateLogic,
				label: LAST_30_DAYS_FILTER_KEY,
			};
			break;
		case TODAY_FILTER_KEY:
			filter = {
				startDate: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0),
				endDate: new Date(
					now.getFullYear(),
					now.getMonth(),
					now.getDate(),
					now.getHours(),
					now.getMinutes(),
					now.getSeconds(),
					now.getMilliseconds(),
				),
				dateLogic: DATEFILTERS.TODAY_FILTER_KEY.dateLogic,
				label: TODAY_FILTER_KEY,
			};
			break;
		case THIS_MONTH_FILTER_KEY:
			filter = {
				startDate: new Date(now.getFullYear(), now.getMonth(), 1),
				endDate: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59, 999),
				dateLogic: DATEFILTERS.THIS_MONTH_FILTER_KEY.dateLogic,
				label: THIS_MONTH_FILTER_KEY,
			};
			break;
		case YESTERDAY_FILTER_KEY:
			filter = {
				startDate: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0, 0),
				endDate: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59, 999),
				dateLogic: DATEFILTERS.YESTERDAY_FILTER_KEY.dateLogic,
				label: YESTERDAY_FILTER_KEY,
			};
			break;
		case LAST_MONTH_FILTER_KEY:
			filter = {
				startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0),
				endDate: new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999),
				dateLogic: DATEFILTERS.LAST_MONTH_FILTER_KEY.dateLogic,
				label: LAST_MONTH_FILTER_KEY,
			};
			break;
		case CUSTOMIZED_FILTER_KEY:
			filter = {
				startDate: new Date(),
				endDate: new Date(),
				dateLogic: DATEFILTERS.CUSTOMIZED_FILTER_KEY.dateLogic,
				label: CUSTOMIZED_FILTER_KEY,
			};
			break;
	}

	const formattedFilter: DateFilter = {
		startDate: filter['startDate'] instanceof Date ? toISODateTimeLocal(filter['startDate']) : '',
		endDate: filter['endDate'] instanceof Date ? toISODateTimeLocal(filter['endDate']) : '',
		dateLogic: filter['dateLogic']?.toString() || '',
		label: typeof filter['label'] === 'string' ? filter['label'] : '',
	};

	return formattedFilter;
};

/**
 * Resolves a DateFilter to one with fresh computed dates.
 * For label-based filters (today, yesterday, last_7_days, …), dates are always
 * recomputed from the label at call time so stale timestamps from localStorage
 * are never sent to the API.
 * Custom (user-picked) ranges are returned as-is.
 */
export const resolveDateFilter = <T extends DateFilter | null | undefined>(filter: T): T => {
	if (!filter) return filter;
	if (!filter.label || filter.label === CUSTOMIZED_FILTER_KEY) return filter;
	return handleFilterChange(filter.label) as T;
};
